<template>
	<div class="flex gap-2">
		<USelectMenu
			v-model="countryCodeOption"
			:items="countryCodeSelectItems"
			value-key="value"
			:size="size"
			:name="country_code_name"
			:ui="{ content: 'w-auto min-w-48' }"
			class="shrink-0 w-auto"
		>
			<template #default>
				{{ theCountryCode ? countryCodeToEmoji(theCountryCode) : '' }}
			</template>
		</USelectMenu>
		<UInput
			ref="inputRef"
			v-model="formattedValue"
			placeholder="(555) 555-5555"
			type="text"
			inputmode="tel"
			:autocomplete="autocomplete"
			:name="phone_number_name"
			:size="size"
			class="flex-1"
			@keyup.enter="emit('submit')"
		>
			<template #leading>
				<span
					class="text-sm text-muted"
					:class="{ 'opacity-50': !countryCallingCode }"
				>+{{ countryCallingCode }}</span>
			</template>
		</UInput>
	</div>
</template>

<script setup lang="ts">
import { parsePhoneNumberFromString, type CountryCode, type PhoneNumber, type E164Number, type CountryCallingCode, getCountryCallingCode } from "libphonenumber-js";
import countries from "../assets/countries";

interface CountryCodeOption {
	text: string;
	value: CountryCode;
	emoji: string;
}

const countryCodeToEmoji = (input: CountryCode): string => input.replace(/./g, (x) => String.fromCharCode(55356, 56741 + x.charCodeAt(0)));

const countryCodeOptions = ([...Object.entries(countries)] as unknown as [CountryCode, string][])
	.map(([value, text]): CountryCodeOption => ({
		text,
		value,
		emoji: countryCodeToEmoji(value),
	}))
	.sort((a, b) => a.text.localeCompare(b.text))
	.sort(({ value: a }, { value: b }) => {
		const preferred = ["CA", "US"].reverse();
		return preferred.indexOf(a) * -1 - preferred.indexOf(b) * -1;
	});

const countryCodeSelectItems = countryCodeOptions.map((option) => ({
	label: `${option.emoji} ${option.text}`,
	value: option.value,
	emoji: option.emoji,
}));

const props = withDefaults(defineProps<{
	modelValue?: string | null;
	country_code_name?: string;
	phone_number_name?: string;
	autocomplete?: string;
	size?: "sm" | "md" | "lg";
	countryCode?: CountryCode | null;
}>(), {
	modelValue: "",
	countryCode: "CA",
	size: "md",
});

const emit = defineEmits<{
	"update:modelValue": [value: E164Number | null];
	submit: [];
}>();

const inputRef = ref<ComponentPublicInstance | null>(null);
const formattedValue = ref("");
const theCountryCode = ref<CountryCode | undefined>(props.countryCode ?? undefined);

const countryCodeOption = computed({
	get(): CountryCode | undefined {
		return theCountryCode.value;
	},
	set(input: CountryCode | undefined) {
		theCountryCode.value = input;
	},
});

const cleanValue = computed(() => {
	return formattedValue.value.replace(/[()\\s\\-\\.]/g, "");
});

const countryCallingCode = computed<CountryCallingCode | "">(() => {
	return theCountryCode.value && getCountryCallingCode(theCountryCode.value) || "";
});

const countryCodeRegex = computed(() => {
	return new RegExp(`^\\+${countryCallingCode.value || ""}`);
});

const phoneNumber = computed<PhoneNumber | null>(() => {
	return parsePhoneNumberFromString(cleanValue.value, theCountryCode.value) || null;
});

const number = computed<E164Number | null>(() => {
	return phoneNumber.value && phoneNumber.value.isValid() ? phoneNumber.value.number : null;
});

// Watch for changes to countryCode prop
watch(() => props.countryCode, (val) => {
	theCountryCode.value = val ?? undefined;
});

// Watch for changes to modelValue prop
watch(() => props.modelValue, (value) => {
	if (!value) return;

	const parsed = parsePhoneNumberFromString(value);
	if (parsed) {
		formattedValue.value = parsed.formatNational().replace(countryCodeRegex.value, "").trim();
		theCountryCode.value = parsed.country;
	}
}, {
	immediate: true,
});

// When country code changes, focus the input
watch(theCountryCode, () => {
	inputRef.value?.$el?.querySelector("input")?.focus();
});

// Emit number changes
watch(number, (val) => {
	emit("update:modelValue", val);
});
</script>
